import React, { useEffect, useState, useMemo, useRef } from 'react';
import { LuEye, LuFileText } from 'react-icons/lu';
import ReactQuill, { Quill } from 'react-quill-new'; 
import ImageResize from 'quill-image-resize-module-react';
import 'react-quill-new/dist/quill.snow.css';
import '../../styles/AdminNewsTab.css';

window.Quill = Quill;
Quill.register('modules/imageResize', ImageResize);

const API_BASE = process.env.REACT_APP_API_BASE || '';

const FOOTER_TEMPLATES = {
  kr: `<p><br></p><p>전문보기</p>`,
  en: `<p><br></p><p>View All Article</p>`,
  jp: `<p><br></p><p>記事全文を読む</p>`,
  ch: `<p><br></p><p>閱讀全文</p>`
};

export default function AdminNewsTab() {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState('');
  const [activeLang, setActiveLang] = useState('kr'); 
  const [uploadedImages, setUploadedImages] = useState([]);
  
  const getTemplateForm = () => ({
    id: null, 
    date: new Date().toISOString().split('T')[0],
    category: 'NEWS', 
    thumbnail: '',
    is_visible: 1,
    title_kr: '', title_en: '', title_jp: '', title_ch: '',
    content_kr: '', content_en: '', content_jp: '', content_ch: ''
  });
  
  const quillRefs = {
    kr: useRef(null), en: useRef(null), jp: useRef(null), ch: useRef(null)
  };

  // const initialForm = { 
  //   id: null, date: new Date().toISOString().split('T')[0],
  //   category: 'NEWS', thumbnail: '',
  //   title_kr: '', title_en: '', title_jp: '', title_ch: '',
  //   content_kr: '', content_en: '', content_jp: '', content_ch: ''
  // };
  
  const [n, setN] = useState(getTemplateForm);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const imageHandler = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch(`${API_BASE}/api/admin_image_upload.php`, {
          method: 'POST', body: formData, credentials: 'include'
        });
        const data = await res.json();

        const currentQuill = quillRefs[activeLang].current.getEditor();
        const range = currentQuill.getSelection(true);
        const imageUrl = data.url.startsWith('http') ? data.url : `${API_BASE}${data.url}`;
        
        currentQuill.insertEmbed(range.index, 'image', imageUrl);
        currentQuill.setSelection(range.index + 1);

        setUploadedImages(prev => prev.includes(imageUrl) ? prev : [...prev, imageUrl]);
      } catch (e) { alert('이미지 업로드 실패'); }
    };
  };

  const handleCancel = async () => {
    if (uploadedImages.length > 0) {
      const confirmCancel = window.confirm('작성 중인 내용과 업로드된 이미지가 모두 삭제됩니다. 취소하시겠습니까?');
      if (!confirmCancel) return;

      try {
        // 서버의 cleanup API 호출 (배열로 전달)
        await fetch(`${API_BASE}/api/admin_image_delete.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ urls: uploadedImages }),
          credentials: 'include'
        });
      } catch (e) {
        console.error("이미지 정리 실패:", e);
      }
    }
    
    setShowModal(false);
    setUploadedImages([]);
    setN(getTemplateForm());
  };

  const appendTemplateToAll = () => {
    if (!window.confirm('모든 언어 본문 하단에 "전문보기" 문구를 추가할까요?')) return;

    const langs = ['kr', 'en', 'jp', 'ch'];
    const updatedForm = { ...n };

    langs.forEach(lang => {
      const currentContent = updatedForm[`content_${lang}`] || '';
      // 이미 전문보기가 들어있는지 체크 (중복 방지)
      const footerText = FOOTER_TEMPLATES[lang];
      if (!currentContent.includes(footerText.replace('<p><br></p>', ''))) {
        updatedForm[`content_${lang}`] = currentContent + footerText;
      }
    });

    setN(updatedForm);
    setMsg('문구가 추가되었습니다. ✅');
    setTimeout(() => setMsg(''), 2000);
  };

  const copyToAllLangs = (url) => {
    const langs = ['kr', 'en', 'jp', 'ch'];
    langs.forEach(lang => {
      if (lang === activeLang) return;
      
      const editor = quillRefs[lang].current.getEditor();
      const range = editor.getSelection() || { index: editor.getLength() };
      editor.insertEmbed(range.index, 'image', url);
    });
    setMsg('이미지가 추가되었습니다. ✅');
    setTimeout(() => setMsg(''), 2000);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        ['image', 'link'], ['clean']
      ],
      handlers: { image: imageHandler }
    },
    imageResize: { modules: ['Resize', 'DisplaySize', 'Toolbar'] }
  }), [activeLang]);

  const load = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin_news_list.php`, { credentials: 'include' });
      const data = await res.json();
      setItems(Array.isArray(data) ? data.sort((a,b) => b.id - a.id) : []);
    } catch (e) { setMsg('로드 실패'); }
  };

  useEffect(() => { load(); }, []);

  // 저장 시 썸네일 추출 (한국어 우선, 없으면 다른 언어 순회)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const langs = ['kr', 'en', 'jp', 'ch'];
    let finalThumbnail = '';
    for (const l of langs) {
      const match = n[`content_${l}`]?.match(/<img [^>]*src="([^"]+)"[^>]*>/);
      if (match) { finalThumbnail = match[1]; break; }
    }
    
    const finalData = { ...n, thumbnail: finalThumbnail };

    try {
      const url = isEditMode ? `${API_BASE}/api/admin_news_update.php` : `${API_BASE}/api/admin_news_create.php`;
      const res = await fetch(url, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        credentials: 'include', body: JSON.stringify(finalData)
      });
      if(res.ok) {
        setShowModal(false); load(); setMsg('저장 성공 ✅');
        setTimeout(() => setMsg(''), 2000);
      }
    } catch (error) { alert('저장 실패'); }
  };

  // ✅ 언어별 입력 여부 체크 함수
  const isFilled = (lang) => {
    const title = n[`title_${lang}`]?.trim();
    const content = n[`content_${lang}`]?.replace(/<[^>]*>?/gm, '').trim();
    // const templateText = NEWS_TEMPLATES[lang].content.replace(/<[^>]*>?/gm, '').trim();

    // const hasTitle = title.length > 0;
    // const hasActualContent = content.length > 0 && content !== templateText;
    
    return title.length > 0 && content.length > 0;
  };

  // ✅ 개별 에디터 섹션 (display: none으로 유지)
  const renderLangSection = (lang) => (
    <div key={lang} style={{ display: activeLang === lang ? 'block' : 'none' }}>
      {/* <div className="input-group">
        <label>제목 ({lang.toUpperCase()})</label>
        <input 
          type="text" 
          value={n[`title_${lang}`]} 
          onChange={e => setN({...n, [`title_${lang}`]: e.target.value})} 
          placeholder={`${lang.toUpperCase()} 제목을 입력하세요`}
          required={lang === 'kr'}
        />
      </div> */}
      <div className="quill-outer-wrapper">
        <label className="editor-label label">내용 ({lang.toUpperCase()})</label>
        <ReactQuill 
          ref={quillRefs[lang]}
          theme="snow" 
          modules={modules} 
          value={n[`content_${lang}`] || ''} 
          onChange={val => setN(prev => ({...prev, [`content_${lang}`]: val}))} 
        />
      </div>
    </div>
  );

  return (
    <div className="admin-tab-content news-admin">
      {msg && <div className="admin-toast success">{msg}</div>}
      
      <div className="admin-list-header">
        <button onClick={() => {setN(getTemplateForm()); setIsEditMode(false); setShowModal(true); setActiveLang('kr');}} className="admin-btn primary">+ NEW NEWS</button>
      </div>

      <section className='admin-card'>
        <div class="card-head">
          <h3>NEWS</h3>
          <span class="card-dot"></span>
        </div>
        <div className="admin-news-list">
          {items.map(it => (
            <div key={it.id} className="news-admin-item">
              <div className="item-thumb">
                {it.thumbnail ? <img src={it.thumbnail} alt="thumb" /> : <div className="no-img">No Image</div>}
              </div>
              <div className="item-info">
                <div className='item-meta-row'>
                  <span className="item-date">{it.date}</span>
                  <span className="item-date">{Number(it.is_visible) === 1 ? '노출' : '숨김'}</span>
                  <span className="item-views">
                    <LuEye size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    {Number(it.views || 0).toLocaleString()}
                  </span>
                </div>
                <h4 className="item-title">{it.title_kr || '제목 없음'}</h4>
              </div>
              <div className="item-actions">
                <button onClick={() => {setN(it); setIsEditMode(true); setActiveLang('kr'); setShowModal(true); setUploadedImages([]);}} className="icon-btn edit">✎</button>
                <button onClick={async () => { if(window.confirm('삭제하시겠습니까?')) { await fetch(`${API_BASE}/api/admin_news_delete.php`, {method:'POST', body: JSON.stringify({id: it.id}), credentials:'include'}); load(); }}} className="icon-btn del">✕</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-card modal-card news-modal">
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="modal-header">
                <h3>{isEditMode ? 'EDIT NEWS' : 'CREATE NEWS'}</h3>

                <div className="lang-selector-wrapper">
                  <button type="button" onClick={appendTemplateToAll} className="admin-btn template-btn">
                    <LuFileText size={16} /> 전문보기
                  </button>
                  <select value={activeLang} onChange={(e) => setActiveLang(e.target.value)} className="lang-select-box">
                    <option value="kr">KR {isFilled('kr') ? '✅' : ''}</option>
                    <option value="en">EN {isFilled('en') ? '✅' : ''}</option>
                    <option value="jp">JP {isFilled('jp') ? '✅' : ''}</option>
                    <option value="ch">CH {isFilled('ch') ? '✅' : ''}</option>
                  </select>
                </div>
              </div>

              {uploadedImages.length > 0 && (
                <div className="uploaded-images-bar">
                  <span className="bar-label">이미지:</span>
                  <div className="img-chips">
                    {uploadedImages.map((url, i) => (
                      <div key={i} className="img-chip" onClick={() => copyToAllLangs(url)}>
                        <img src={url} alt="uploaded" />
                        <span className="chip-hover">추가</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="input-row inline-row">
                <div className="input-group date-group">
                  <label>날짜</label>
                    <input 
                      type="date" 
                      value={n.date} 
                      onChange={e => setN({...n, date: e.target.value})} 
                      required 
                    />
                  </div>
                  <div className="input-group title-group">
                    <label>제목</label>
                    <input 
                      type="text" 
                      value={n[`title_${activeLang}`]} 
                      onChange={e => setN({...n, [`title_${activeLang}`]: e.target.value})} 
                      placeholder="제목을 입력하세요"
                      required={activeLang === 'kr'}
                    />
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-end', gap: '8px', whiteSpace: 'nowrap', marginLeft: 'auto', minHeight: '42px' }}>
                    <input
                      type="checkbox"
                      checked={Number(n.is_visible) === 1}
                      onChange={e => setN({ ...n, is_visible: e.target.checked ? 1 : 0 })}
                    />
                    VIEW
                  </label>
                </div>

              {/* ✅ 4개 언어의 섹션을 모두 렌더링하되 스타일로 숨김 처리 */}
              <div className="lang-specific-section">
                {renderLangSection('kr')}
                {renderLangSection('en')}
                {renderLangSection('jp')}
                {renderLangSection('ch')}
              </div>

              <div className="form-actions">
                <button type="submit" className="admin-btn primary">SAVE</button>
                <button type="button" onClick={handleCancel} className="admin-btn secondary">CANCEL</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
